/*
 * Redeluxe strict handbag search.
 *
 * Shopify's native search matches every word against title AND description
 * (measurements such as "10.25" become the token "25") and relaxes the match,
 * so "Birkin 25" also returns Birkin 30/35s, Kelly Sellier 25s and HAC 32s.
 *
 * This script interprets recognisable model / size queries and rewrites them
 * into Shopify's own field-scoped boolean syntax, e.g.
 *     Birkin 25  ->  title:birkin AND title:25
 * The rewrite happens BEFORE the request is made, so the store itself does the
 * filtering, counts, sorting and pagination -- nothing is hidden client-side.
 * Queries that are not recognised are passed through untouched.
 *
 * Used by: header search form, predictive search (fetch), the search page form,
 * and direct /search?q= links (canonicalised on load).
 */
(function (root) {
  'use strict';

  // ---------------------------------------------------------------- dictionary
  var Q = function (w) { return 'title:' + w; };
  var OR = function () { return '(' + Array.prototype.slice.call(arguments).join(' OR ') + ')'; };
  var PHRASE = function (p) { return 'title:"' + p + '"'; };
  // Set to true once the rx-* classification tags (search-audit/) have been applied to the products.
  // Chanel 19 / 22 / 25 tags were applied to all 331 products on 2026-09-19 (search-audit/backfill/README.md).
  var TAGS_LIVE = true;

  var WORD_SIZES = ['mini', 'small', 'medium', 'large', 'jumbo', 'maxi'];
  var NOT_SLG = ['NOT ' + Q('card'), 'NOT ' + Q('holder'), 'NOT ' + Q('coin'), 'NOT ' + Q('charm')];

  /*
   * label      shown to the shopper
   * phrases    token sequences that select this model (longest match wins)
   * must       always-required terms
   * mustNot    always-excluded terms (accessories, other brands' namesakes)
   * sizeKind   'cm' -> numeric size directly after the model; 'word' -> Small/Medium/Large...; none -> no size
   * sizes      standard sizes offered as "other sizes" when the requested one is unavailable
   * words      construction / variation words that may be typed and are matched in the title
   */
  var KELLY_NOT = ['NOT ' + Q('wallet'), 'NOT ' + Q('go'), 'NOT ' + Q('pocket'), 'NOT ' + Q('shopper'), 'NOT ' + Q('shopping'), 'NOT ' + Q('nano'),
                   'NOT ' + Q('belt'), 'NOT ' + Q('mini'), 'NOT ' + Q('so'), 'NOT ' + Q('danse'), 'NOT ' + Q('cut'), 'NOT ' + Q('quilted')];

  var MODELS = [
    // ---- Hermes ----
    { id: 'kelly-to-go', label: 'Hermès Kelly Wallet To Go', phrases: [['kelly', 'wallet', 'to', 'go'], ['kelly', 'to', 'go'], ['kelly', 'go']],
      must: [Q('kelly'), Q('go')], mustNot: ['NOT ' + Q('shopper'), 'NOT ' + Q('shopping'), 'NOT ' + Q('quilted')] },
    { id: 'kelly-wallet', label: 'Hermès Kelly Wallet', phrases: [['kelly', 'pocket', 'long', 'wallet'], ['kelly', 'long', 'wallet'], ['kelly', 'wallet'], ['kelly', 'pocket']],
      must: [Q('kelly'), OR(Q('wallet'), Q('go'), Q('pocket'))], mustNot: ['NOT ' + Q('shopper'), 'NOT ' + Q('shopping'), 'NOT ' + Q('quilted')] },
    { id: 'chanel-kelly-shopper', label: 'Chanel Kelly Shopper', phrases: [['nano', 'kelly', 'shopper'], ['kelly', 'shopper'], ['kelly', 'shopping'], ['nano', 'kelly'], ['chanel', 'kelly']],
      must: [Q('kelly'), OR(Q('shopper'), Q('shopping'), Q('nano'))], mustNot: [] },
    { id: 'mini-kelly', label: 'Hermès Mini Kelly', phrases: [['mini', 'kelly'], ['kelly', 'mini']],
      must: [OR(PHRASE('mini kelly'), PHRASE('kelly mini'))], mustNot: ['NOT ' + Q('wallet'), 'NOT ' + Q('shopper'), 'NOT ' + Q('shopping'), 'NOT ' + Q('nano'), 'NOT ' + Q('quilted')], sizeKind: 'cm', sizes: [15, 20], words: ['sellier', 'retourne'] },
    { id: 'so-kelly', label: 'Hermès So Kelly', phrases: [['so', 'kelly']], must: [PHRASE('so kelly')], mustNot: [], sizeKind: 'cm', sizes: [22, 26] },
    { id: 'kelly-danse', label: 'Hermès Kelly Danse', phrases: [['kelly', 'danse']], must: [Q('kelly'), Q('danse')], mustNot: [] },
    { id: 'kelly', label: 'Hermès Kelly', phrases: [['kelly']], must: [Q('kelly')], mustNot: KELLY_NOT, sizeKind: 'cm', sizes: [25, 28, 32, 35], words: ['sellier', 'retourne', 'touch'], shorthand: 'k' },
    { id: 'birkin', label: 'Hermès Birkin', phrases: [['birkin']], must: [Q('birkin')], mustNot: [], sizeKind: 'cm', sizes: [25, 30, 35, 40], words: ['sellier', 'touch', 'shoulder'], shorthand: 'b' },
    { id: 'hac', label: 'Hermès HAC', phrases: [['haut', 'a', 'courroies'], ['hac']], must: [Q('hac')], mustNot: [], sizeKind: 'cm', sizes: [28, 32, 40] },
    { id: 'constance-to-go', label: 'Hermès Constance Wallet To Go', phrases: [['constance', 'wallet', 'to', 'go'], ['constance', 'to', 'go'], ['constance', 'long', 'to', 'go'], ['constance', 'go']],
      must: [Q('constance'), Q('go')], mustNot: ['NOT ' + Q('belt')] },
    { id: 'constance-wallet', label: 'Hermès Constance Wallet', phrases: [['constance', 'slim', 'wallet'], ['constance', 'slim'], ['constance', 'wallet']],
      must: [Q('constance'), OR(Q('wallet'), Q('go'), Q('slim'))], mustNot: ['NOT ' + Q('belt')] },
    { id: 'constance-elan', label: 'Hermès Constance Elan', phrases: [['constance', 'elan'], ['elan']], must: [Q('elan')], mustNot: [], sizeKind: 'cm' },
    { id: 'constance', label: 'Hermès Constance', phrases: [['constance']], must: [Q('constance')],
      mustNot: ['NOT ' + Q('wallet'), 'NOT ' + Q('go'), 'NOT ' + Q('slim'), 'NOT ' + Q('belt'), 'NOT ' + Q('long')], sizeKind: 'cm', sizes: [18, 23, 24], words: [] },
    { id: 'picotin', label: 'Hermès Picotin', phrases: [['picotin'], ['pictoin']], must: [OR(Q('picotin'), Q('pictoin'))], mustNot: [], sizeKind: 'cm', sizes: [18, 22], words: ['lock', 'cargo', 'eclat'] },
    { id: 'evelyne', label: 'Hermès Evelyne', phrases: [['evelyne']], must: [Q('evelyne')], mustNot: [], sizeKind: 'cm', sizes: [16, 29, 33], words: ['amazone', 'pocket'],
      sizeMap: { tpm: OR(Q('16'), Q('tpm')), '16': OR(Q('16'), Q('tpm')) } },
    { id: 'plume', label: 'Hermès Plume', phrases: [['plume']], must: [Q('plume')], mustNot: [], sizeKind: 'cm', sizes: [21, 28] },
    { id: 'roulis', label: 'Hermès Roulis', phrases: [['roulis']], must: [Q('roulis')], mustNot: ['NOT ' + Q('wallet'), 'NOT ' + Q('belt')], sizeKind: 'cm', sizes: [18] },
    { id: 'cabasellier', label: 'Hermès Cabasellier', phrases: [['cabasellier']], must: [Q('cabasellier')], mustNot: [], sizeKind: 'cm' },
    { id: 'della-cavalleria', label: 'Hermès Della Cavalleria', phrases: [['della', 'cavalleria']], must: [PHRASE('della cavalleria')], mustNot: [], sizeKind: 'cm' },
    { id: 'lindy', label: 'Hermès Lindy', phrases: [['lindy']], must: [Q('lindy')], mustNot: [], sizeKind: 'cm', sizes: [26, 30] },
    { id: 'garden-party', label: 'Hermès Garden Party', phrases: [['garden', 'party']], must: [PHRASE('garden party')], mustNot: [], sizeKind: 'cm', sizes: [30, 36] },
    { id: 'herbag', label: 'Hermès Herbag', phrases: [['herbag']], must: [Q('herbag')], mustNot: [], sizeKind: 'cm' },
    { id: 'bolide', label: 'Hermès Bolide', phrases: [['bolide']], must: [Q('bolide')], mustNot: [], sizeKind: 'cm' },
    { id: 'jypsiere', label: 'Hermès Jypsière', phrases: [['jypsiere']], must: [Q('jypsiere')], mustNot: [], sizeKind: 'cm' },
    // ---- Chanel ----
    { id: 'coco-chain-handle', label: 'Chanel Coco Chain Handle', phrases: [['coco', 'chain', 'handle']], must: [PHRASE('coco chain handle')], mustNot: [], sizeKind: 'word', sizes: [] },
    { id: 'coco-handle', label: 'Chanel Coco Handle', phrases: [['coco', 'handle']], must: [PHRASE('coco handle')], mustNot: [], sizeKind: 'word', sizes: [] },
    { id: 'classic-flap', label: 'Chanel Classic Flap', phrases: [['classic', 'double', 'flap'], ['classic', 'flap'], ['timeless', 'flap']], must: [OR(PHRASE('classic flap'), PHRASE('classic double flap'), PHRASE('classic single flap'))], mustNot: NOT_SLG.concat(['NOT ' + Q('wallet')]), sizeKind: 'word', sizes: [] },
    { id: 'wallet-on-chain', label: 'Chanel Wallet on Chain', phrases: [['wallet', 'on', 'chain'], ['woc']], must: [OR(PHRASE('wallet on chain'), Q('woc'))], mustNot: [], sizeKind: 'none' },
    { id: 'trendy-cc', label: 'Chanel Trendy CC', phrases: [['trendy', 'cc'], ['trendy']], must: [Q('trendy')], mustNot: [], sizeKind: 'word', sizes: [] },
    { id: 'business-affinity', label: 'Chanel Business Affinity', phrases: [['business', 'affinity']], must: [PHRASE('business affinity')], mustNot: [], sizeKind: 'word', sizes: [] },
    { id: 'duma', label: 'Chanel Duma', phrases: [['duma']], must: [Q('duma')], mustNot: [], sizeKind: 'word', sizes: [] },
    // Chanel season codes (19S, 22A, 25B...) are split by the search engine into a bare number, so the number in the
    // model names "19" / "22" / "25" cannot be told apart from a season code by title alone (and OR-ing phrases makes
    // the engine relax them). Until the classification tags are applied (TAGS_LIVE) these three models use the single
    // most precise title phrase: nothing wrong is shown, but a few listings whose title uses a different wording
    // ("Mini 22 Bag", "22 Small Hobo") are not returned. With the tags applied the match is exact and complete.
    { id: 'chanel-19', label: 'Chanel 19', phrases: [['chanel', '19'], ['19', 'flap'], ['19', 'bag'], ['19', 'handbag']],
      tags: ['rx-chanel-chanel-19', 'rx-form-flap'], must: [PHRASE('19 flap')], mustNot: NOT_SLG.concat(['NOT ' + Q('backpack')]), sizeKind: 'word', sizes: [], numeric: true },
    // A 19 Wallet on Chain is in the 19 family AND is a Wallet on Chain, but never in "Chanel 19" (flap bag) results.
    { id: 'chanel-19-woc', label: 'Chanel 19 Wallet on Chain', phrases: [['chanel', '19', 'wallet', 'on', 'chain'], ['19', 'wallet', 'on', 'chain'], ['chanel', '19', 'woc'], ['19', 'woc']],
      tags: ['rx-chanel-chanel-19', 'rx-form-wallet-on-chain'], must: [PHRASE('19 wallet on chain')], mustNot: [], sizeKind: 'none' },
    { id: 'chanel-22', label: 'Chanel 22', phrases: [['chanel', '22'], ['22', 'bag'], ['22', 'hobo'], ['22', 'handbag']],
      tags: ['rx-chanel-chanel-22'], must: [PHRASE('22 hobo')], mustNot: NOT_SLG, sizeKind: 'word', sizes: [], numeric: true, words: ['hobo', 'drawstring', 'backpack'], formWords: true },
    { id: 'chanel-25', label: 'Chanel 25', phrases: [['chanel', '25'], ['25', 'hobo']],
      tags: ['rx-chanel-chanel-25'], must: [PHRASE('25 hobo')], mustNot: NOT_SLG, sizeKind: 'word', sizes: [], numeric: true, words: ['hobo'], formWords: true },
    { id: 'boy', label: 'Chanel Boy', phrases: [['boy', 'bag'], ['le', 'boy'], ['leboy'], ['boy', 'chanel'], ['chanel', 'boy']], must: [OR(Q('boy'), Q('leboy'))], mustNot: ['NOT ' + Q('coin'), 'NOT ' + Q('card'), 'NOT ' + Q('holder')], sizeKind: 'word', sizes: [] },
    { id: 'mini-rectangular', label: 'Chanel Mini Rectangular', phrases: [['mini', 'rectangular'], ['rectangular', 'mini']], must: [OR(PHRASE('mini rectangular'), PHRASE('rectangular mini'))], mustNot: [], sizeKind: 'none' },
    { id: 'mini-square', label: 'Chanel Mini Square', phrases: [['mini', 'square']], must: [PHRASE('mini square')], mustNot: [], sizeKind: 'none' },
    { id: 'vanity-case', label: 'Chanel Vanity Case', phrases: [['vanity', 'case'], ['vanity']], must: [Q('vanity')], mustNot: [], sizeKind: 'word', sizes: [] },
    { id: 'reissue', label: 'Chanel Reissue 2.55', phrases: [['reissue']], must: [Q('reissue')], mustNot: [], sizeKind: 'none' }
  ];

  // Models whose phrase is a bare number ("19") only match when 'chanel' or a bag word is present (encoded in the phrases above).
  var BRAND_TOKENS = { hermes: 1, chanel: 1 };
  var NOISE = { bag: 1, bags: 1, handbag: 1, handbags: 1, the: 1, de: 1, size: 1, cm: 1, hermes: 1 };
  var COLOR_PAIRS = { black: 'noir', noir: 'black', white: 'blanc', blanc: 'white', blue: 'bleu', bleu: 'blue', green: 'vert', vert: 'green',
                      red: 'rouge', rouge: 'red', yellow: 'jaune', jaune: 'yellow', grey: 'gris', gray: 'gris', gris: 'grey' };
  // Payment / deposit placeholder products are never model listings.
  var PLACEHOLDER_NOT = ['NOT ' + Q('installment'), 'NOT ' + Q('partial'), 'NOT ' + Q('deposit')];
  var MATERIALS = {};
  ('togo epsom swift clemence veau box ardennes fjord evercolor negonda barenia madame tadelakt chevre ostrich crocodile niloticus porosus alligator lizard ' +
   'canvas denim caviar lambskin calfskin goatskin patent tweed suede').split(' ').forEach(function (w) { MATERIALS[w] = 1; });
  // The only compact shorthands that are expanded. They are exact: B25 = Birkin 25, K28 = Kelly 28.
  var SHORTHAND = { b: { model: 'birkin', sizes: [25, 30, 35, 40] }, k: { model: 'kelly', sizes: [25, 28, 32, 35] } };

  // ---------------------------------------------------------------- tokenising
  function fold(s) {
    s = String(s == null ? '' : s);
    try { s = s.normalize('NFD').replace(/[̀-ͯ]/g, ''); } catch (e) { /* very old browsers */ }
    return s.toLowerCase();
  }

  function tokenize(q) {
    var s = fold(q).replace(/[‘’'`]/g, '').replace(/[^a-z0-9.\s]/g, ' ').replace(/\.(?!\d)/g, ' ');
    var raw = s.split(/\s+/).filter(Boolean);
    var out = [];
    raw.forEach(function (t) {
      var m;
      if ((m = /^(\d{1,2})cm$/.exec(t))) { out.push(m[1]); return; }               // 25cm -> 25
      if ((m = /^([a-z])(\d{2})$/.exec(t)) && SHORTHAND[m[1]] && SHORTHAND[m[1]].sizes.indexOf(+m[2]) > -1) {
        out.push(SHORTHAND[m[1]].model, m[2]); return;                                // b25 -> birkin 25, k28 -> kelly 28
      }
      if ((m = /^([a-z]{3,})(\d{1,2})(?:cm)?$/.exec(t)) && knownWord(m[1])) { out.push(m[1], m[2]); return; } // birkin25, kelly28cm
      out.push(t);
    });
    return out;
  }

  var KNOWN = null;
  function knownWord(w) {
    if (!KNOWN) {
      KNOWN = {};
      MODELS.forEach(function (m) { m.phrases.forEach(function (p) { p.forEach(function (t) { if (/^[a-z]{3,}$/.test(t)) KNOWN[t] = 1; }); }); });
      KNOWN.hac = 1;
    }
    return !!KNOWN[w];
  }

  // ---------------------------------------------------------------- interpreter
  function findModel(tokens) {
    var best = null;
    MODELS.forEach(function (m) {
      m.phrases.forEach(function (p) {
        for (var i = 0; i + p.length <= tokens.length; i++) {
          var ok = true;
          for (var j = 0; j < p.length; j++) if (tokens[i + j] !== p[j]) { ok = false; break; }
          if (ok) {
            var score = p.length * 10 + (m.numeric ? 0 : 1);
            if (!best || score > best.score) best = { model: m, start: i, len: p.length, score: score };
          }
        }
      });
    });
    return best;
  }

  function colorTerm(w) {
    return COLOR_PAIRS[w] ? OR(Q(w), Q(COLOR_PAIRS[w])) : Q(w);
  }

  // With the rx-* tags live, models that have tags are matched on them (exact, and complete for Chanel 19 / 22 / 25);
  // otherwise on the title words.
  function baseTerms(m) {
    return (TAGS_LIVE && m.tags) ? m.tags.map(function (t) { return 'tag:' + t; }) : m.must.slice();
  }
  function sizeTerm(m, s) {
    if (TAGS_LIVE && m.tags) return 'tag:rx-size-' + String(s).toLowerCase();
    return m.sizeMap && m.sizeMap[s] ? m.sizeMap[s] : Q(s);
  }
  function extraTerm(m, w) {
    return (TAGS_LIVE && m.tags && m.formWords) ? 'tag:rx-form-' + w : Q(w);
  }

  function modelQuery(m) {
    return baseTerms(m).concat(m.mustNot || [], PLACEHOLDER_NOT).join(' AND ');
  }

  function sizeQuery(m, s) {
    return baseTerms(m).concat([sizeTerm(m, s)], m.mustNot || [], PLACEHOLDER_NOT).join(' AND ');
  }

  /** Returns null when the query is not a recognised model query. */
  function interpret(query) {
    var trimmed = String(query == null ? '' : query).trim();
    if (!trimmed || isStrict(trimmed)) return null;
    var tokens = tokenize(trimmed);
    if (!tokens.length) return null;
    var hit = findModel(tokens);
    if (!hit) return null;
    var m = hit.model;

    var rest = tokens.slice(0, hit.start).concat(tokens.slice(hit.start + hit.len));
    rest = rest.filter(function (t) { return !NOISE[t] && !BRAND_TOKENS[t]; });

    // a second model name in the query means we cannot be sure -> leave it to native search
    var second = null;
    rest.forEach(function (t) { var h = findModel([t]); if (h && h.model.id !== m.id && !h.model.numeric) second = h; });
    if (second) return null;

    var size = null, extras = [], colors = [], materials = [], hardware = [], leftover = [];
    // hardware phrases first ("gold hardware", "rose gold plated", "palladium", "ghw"...), so their words are not read as colours
    var HW = [
      { key: 'rosegold', words: ['rose', 'gold'], opt: ['hardware', 'plated'], label: 'rose gold hardware', terms: [PHRASE('rose gold')] },
      { key: 'lightgold', words: ['light', 'gold'], opt: ['hardware'], label: 'light gold hardware', terms: [PHRASE('light gold')] },
      { key: 'agedgold', words: ['aged', 'gold'], opt: ['hardware'], label: 'aged gold hardware', terms: [PHRASE('aged gold')] },
      { key: 'gold', words: ['gold'], req: ['hardware', 'plated'], label: 'gold hardware', terms: [Q('gold'), OR(Q('hardware'), Q('plated'))], not: ['NOT ' + PHRASE('rose gold'), 'NOT ' + PHRASE('light gold'), 'NOT ' + PHRASE('aged gold')] },
      { key: 'gold', words: ['ghw'], label: 'gold hardware', terms: [Q('gold'), OR(Q('hardware'), Q('plated'))], not: ['NOT ' + PHRASE('rose gold'), 'NOT ' + PHRASE('light gold'), 'NOT ' + PHRASE('aged gold')] },
      { key: 'palladium', words: ['palladium'], opt: ['hardware', 'plated'], label: 'palladium hardware', terms: [Q('palladium')] },
      { key: 'palladium', words: ['phw'], label: 'palladium hardware', terms: [Q('palladium')] },
      { key: 'silver', words: ['silver'], req: ['hardware'], label: 'silver hardware', terms: [Q('silver'), Q('hardware')] },
      { key: 'silver', words: ['shw'], label: 'silver hardware', terms: [Q('silver'), Q('hardware')] }
    ];
    var pool = rest.slice();
    HW.forEach(function (h) {
      for (var i = 0; i + h.words.length <= pool.length; i++) {
        var ok = true;
        for (var k = 0; k < h.words.length; k++) if (pool[i + k] !== h.words[k]) { ok = false; break; }
        if (!ok) continue;
        var next = pool[i + h.words.length];
        var len = h.words.length;
        if (h.req && h.req.indexOf(next) === -1) continue;         // "gold" alone is a colour, not hardware
        if ((h.req && h.req.indexOf(next) > -1) || (h.opt && h.opt.indexOf(next) > -1)) len++;
        if (!hardware.some(function (x) { return x.key === h.key; })) hardware.push(h);
        pool.splice(i, len);
        i--;
      }
    });
    pool.forEach(function (t) {
      if (m.sizeKind === 'cm' && /^\d{1,2}$/.test(t) && size === null) { size = t; return; }
      if (m.sizeKind === 'cm' && m.sizeMap && m.sizeMap[t] && size === null) { size = t; return; }
      if ((m.sizeKind === 'word') && WORD_SIZES.indexOf(t) > -1 && size === null) { size = t; return; }
      if (m.words && m.words.indexOf(t) > -1) { extras.push(t); return; }
      if (COLOR_PAIRS[t] || COLOR_WORDS[t]) { colors.push(t); return; }
      if (MATERIALS[t]) { materials.push(t); return; }
      leftover.push(t);
    });
    // a typed word that this model explicitly excludes (e.g. "Constance 18 wallet") contradicts itself -> native
    var excluded = (m.mustNot || []).map(function (t) { return t.replace(/^NOT title:/, ''); });
    for (var j = 0; j < leftover.length; j++) if (excluded.indexOf(leftover[j]) > -1) return null;
    // a bare number that could not be used as a size makes the query ambiguous -> native
    for (var i = 0; i < leftover.length; i++) if (/^\d+(\.\d+)?$/.test(leftover[i])) return null;

    // attributes in the order they are dropped from the end when the full combination is unavailable
    var attrs = [];
    extras.forEach(function (w) { attrs.push({ kind: 'construction', label: capital(w), terms: [extraTerm(m, w)], not: [] }); });
    colors.forEach(function (w) { attrs.push({ kind: 'colour', label: w, terms: [colorTerm(w)], not: [] }); });
    materials.forEach(function (w) { attrs.push({ kind: 'material', label: w, terms: [Q(w)], not: [] }); });
    hardware.forEach(function (h) { attrs.push({ kind: 'hardware', label: h.label, terms: h.terms, not: h.not || [] }); });
    leftover.forEach(function (w) { attrs.push({ kind: 'other', label: w, terms: [Q(w)], not: [] }); });

    function build(useAttrs) {
      var terms = baseTerms(m);
      if (size !== null) terms.push(sizeTerm(m, size));
      var nots = (m.mustNot || []).slice();
      useAttrs.forEach(function (a) { terms = terms.concat(a.terms); nots = nots.concat(a.not); });
      return terms.concat(nots, PLACEHOLDER_NOT).join(' AND ');
    }
    function labelOf(useAttrs) {
      return m.label + (size !== null ? (m.sizeKind === 'cm' ? ' ' + size : ' ' + capital(size)) : '') +
        useAttrs.map(function (a) { return ' · ' + a.label; }).join('');
    }
    // ladder of progressively looser queries: drop the last attribute each step (same model and size throughout)
    var ladder = [];
    for (var n = attrs.length - 1; n >= 0; n--) ladder.push({ query: build(attrs.slice(0, n)), label: labelOf(attrs.slice(0, n)), dropped: attrs.slice(n).map(function (a) { return a.label; }) });

    return {
      model: m,
      size: size,
      extras: extras,
      colors: colors,
      materials: materials,
      hardware: hardware.map(function (h) { return h.label; }),
      leftover: leftover,
      attrs: attrs,
      query: build(attrs),
      label: labelOf(attrs),
      ladder: ladder,
      // same model with a different size / no size: used by the "no exact match" panel
      modelOnly: modelQuery(m),
      sizeQuery: function (s) { return sizeQuery(m, s); }
    };
  }

  var COLOR_WORDS = {};
  ('beige brown pink orange purple violet gold silver navy burgundy camel caramel cream ivory tan taupe etoupe ecru craie rose ' +
   'fuchsia lilac mauve turquoise khaki olive mustard bordeaux marine rouge').split(' ').forEach(function (w) { COLOR_WORDS[w] = 1; });

  function capital(w) { return w ? w.charAt(0).toUpperCase() + w.slice(1) : w; }

  /** Turns an exact query back into the words the shopper typed (mirrors snippets/search-terms-display.liquid). */
  function humanize(strict) {
    var out = [];
    String(strict).split(' AND ').forEach(function (clause) {
      if (/^NOT /.test(clause) || /vendor:/.test(clause)) return;
      // rx-* classification tags (TAGS_LIVE): "tag:rx-chanel-chanel-22 AND tag:rx-size-small" -> "chanel 22 small"
      var tagMatch = /^\(?tag:(rx-[a-z0-9-]+)\)?$/.exec(clause.trim());
      if (tagMatch) {
        var tg = tagMatch[1], part;
        if ((part = /^rx-chanel-chanel-(\d+)$/.exec(tg))) out.push('chanel ' + part[1]);
        else if ((part = /^rx-size-(.+)$/.exec(tg))) out.push(part[1].replace(/-/g, ' '));
        else if ((part = /^rx-form-(.+)$/.exec(tg)) && part[1] !== 'flap') out.push(part[1].replace(/-/g, ' '));
        return;
      }
      out.push(clause.split(' OR ')[0].replace(/[()"]/g, '').replace(/title:/g, '').trim());
    });
    return out.join(' ').trim();
  }

  function isStrict(q) { return /(^|\s)(title|vendor|tag|product_type):/.test(String(q)) && / AND | OR |^title:/.test(String(q)); }

  /** query string the store should actually receive for a typed query */
  function strictQuery(q) {
    var r = interpret(q);
    return r ? r.query : q;
  }

  // ---------------------------------------------------------------- page hooks
  var api = { humanize: humanize, interpret: interpret, strictQuery: strictQuery, isStrict: isStrict, models: MODELS, tokenize: tokenize };
  if (typeof module === 'object' && module.exports) { module.exports = api; return; }
  if (root.RedeluxeSearch) return;
  root.RedeluxeSearch = api;

  var doc = root.document;
  if (!doc) return;

  // 1. Predictive search + any other suggest request: rewrite q on the way out.
  if (typeof root.fetch === 'function') {
    var nativeFetch = root.fetch;
    root.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url);
        if (url && url.indexOf('/search/suggest') > -1) {
          var u = new URL(url, root.location.origin);
          var q = u.searchParams.get('q');
          var r = q && interpret(q);
          if (r) {
            u.searchParams.set('q', r.query);
            input = typeof input === 'string' ? u.pathname + u.search : new Request(u.toString(), input);
          }
        }
      } catch (e) { /* fall through to the untouched request */ }
      return nativeFetch.call(this, input, init);
    };
  }

  // 2. Full search: any search form submit is interpreted first.
  function onSubmit(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    var action = form.getAttribute('action') || '';
    if (action.replace(/\/$/, '') !== '/search' && action.indexOf('/search') !== 0) return;
    var input = form.querySelector('input[name="q"]');
    if (!input) return;
    if (form.querySelector('[aria-selected="true"]')) return; // keyboard-selected suggestion is handled by the theme
    var r = interpret(input.value);
    if (!r) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    var prefix = form.querySelector('input[name="options[prefix]"]');
    var url = '/search?q=' + encodeURIComponent(r.query) + '&type=product&options%5Bprefix%5D=' + encodeURIComponent(prefix ? prefix.value : 'last');
    root.location.assign(url);
  }
  doc.addEventListener('submit', onSubmit, true);

  // 3. Direct /search?q= links (bookmarks, Google, shared URLs) are canonicalised before the page paints.
  try {
    var here = new URL(root.location.href);
    if (here.pathname.replace(/\/$/, '') === '/search' && here.searchParams.has('q')) {
      var typed = here.searchParams.get('q');
      var interpreted = interpret(typed);
      if (interpreted) {
        here.searchParams.set('q', interpreted.query);
        here.searchParams.set('type', 'product');
        here.searchParams.delete('page');
        root.location.replace(here.pathname + here.search);
        return;
      }
    }
  } catch (e) { /* leave the page as is */ }
  // Only reached when no rewrite/redirect was needed: the results on screen are the ones requested.
  var markReady = function () { doc.documentElement.setAttribute('data-rx-search', 'ready'); };
  if (doc.documentElement) markReady(); else doc.addEventListener('DOMContentLoaded', markReady);

  // 4. "No exact match" panel + closest matches. The page (server rendered) has [data-strict-search-empty] when nothing
  //    (in stock) matches, and [data-strict-search-more] on every exact search.
  function searchUrl(q) { return '/search?q=' + encodeURIComponent(q) + '&type=product&options%5Bprefix%5D=last'; }

  function renderAlternatives() {
    var current = new URL(root.location.href).searchParams.get('q') || '';
    var r = isStrict(current) ? interpret(humanize(current)) : null;

    // (a) results exist but the shopper asked for extra attributes: offer the same model and size without them, as a link
    var more = doc.querySelector('[data-strict-search-more]');
    if (more && r && r.attrs.length) {
      var loosest = r.ladder[r.ladder.length - 1];
      var a = more.querySelector('a');
      if (a) { a.href = searchUrl(loosest.query); a.textContent = 'See all ' + loosest.label + ' (any colour, material or hardware)'; more.hidden = false; }
    }

    var hook = doc.querySelector('[data-strict-search-empty]');
    if (!hook) return;
    var ctx = reverse(current);
    if (!ctx) return;

    // (b) closest matches: same model and size, dropping the last-typed attributes one at a time until something exists
    var block = hook.querySelector('[data-alt-block]');
    if (block && r && r.attrs.length && hook.getAttribute('data-empty') === 'true') {
      (function tryLevel(i) {
        if (i >= r.ladder.length) return;
        var level = r.ladder[i];
        root.fetch(searchUrl(level.query) + '&section_id=search-alternatives').then(function (res) { return res.text(); }).then(function (html) {
          var d = new DOMParser().parseFromString(html, 'text/html');
          var box = d.querySelector('[data-alt-results]');
          if (!box || !box.querySelector('.grid-item, .product-item')) return tryLevel(i + 1);
          var count = box.getAttribute('data-count') || '';
          block.querySelector('[data-alt-heading]').textContent = 'Closest matches: ' + level.label + ', without ' + level.dropped.join(', ') + ' (not the full combination you searched for)';
          block.querySelector('[data-alt-products]').innerHTML = box.innerHTML;
          var all = block.querySelector('[data-alt-all]');
          all.href = searchUrl(level.query);
          all.textContent = 'See all ' + count + ' ' + level.label;
          block.hidden = false;
        }).catch(function () { /* no alternatives shown */ });
      })(0);
    }

    // (c) other sizes of the same model: clearly labelled links
    var list = hook.querySelector('[data-alternatives]');
    if (!list) return;
    var links = [];
    if (ctx.size !== null && ctx.model.sizes && ctx.model.sizes.length) {
      ctx.model.sizes.filter(function (s) { return String(s) !== ctx.size; }).forEach(function (s) {
        links.push({ text: ctx.model.label + ' ' + s, q: sizeQuery(ctx.model, String(s)) });
      });
    }
    if (ctx.size !== null || ctx.extras.length || ctx.hasFilters) {
      links.push({ text: 'All ' + ctx.model.label + (ctx.model.sizeKind === 'cm' ? ' sizes' : ''), q: modelQuery(ctx.model) });
    }
    if (!links.length) return;
    list.innerHTML = '';
    links.forEach(function (l) {
      var li = doc.createElement('li');
      var a = doc.createElement('a');
      a.href = searchUrl(l.q);
      a.textContent = l.text;
      li.appendChild(a);
      list.appendChild(li);
    });
    hook.querySelector('[data-alternatives-wrap]').hidden = false;
  }

  // find which model produced a strict query (matches on the model's own "must" prefix)
  function reverse(strict) {
    var best = null;
    MODELS.forEach(function (m) {
      var head = baseTerms(m).join(' AND ');
      if (strict.indexOf(head) === 0 && (!best || head.length > best.head)) best = { m: m, head: head.length };
    });
    if (!best) return null;
    var m = best.m;
    var size = null;
    if (m.sizeKind === 'cm') {
      var mm = /AND title:(\d{1,2})(?: |$)/.exec(strict.slice(best.head)); if (mm) size = mm[1];
      var sm = /AND \(title:(\d{2}) OR title:tpm\)/.exec(strict.slice(best.head)); if (sm) size = sm[1];
    } else if (m.sizeKind === 'word') {
      WORD_SIZES.forEach(function (w) { if (strict.slice(best.head).indexOf('title:' + w) > -1) size = w; });
    }
    var tail = strict.slice(best.head);
    return { model: m, size: size, extras: /AND title:(sellier|retourne|touch|lock)/.test(tail) ? ['x'] : [], colors: [], leftover: [], hasFilters: tail.replace(/ AND NOT [^ ]+/g, '').trim().length > 0 };
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', renderAlternatives); else renderAlternatives();
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this));
